import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RingProgress } from '@mantine/core';
import Modal from 'react-modal';
import { useApiClient } from '../../api/ApiClientContext';
import { StreamDetailsResponse } from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import styles from './StreamDetailsModal.module.scss';

interface StreamDetailsModalProps {
  programmeId: string;
  streamId: string;
  onClose: () => void;
  breadcrumbLabel?: string;
}

type SortColumn = 'name' | 'size' | 'start_date' | 'end_date';
type SortDir = 'asc' | 'desc';

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}`;
};

const ENGAGEMENT_STYLES: Record<string, { color: string; bgColor: string; icon: 'GreenUpIcon' | 'IconDown' | 'BlueArrowRight' }> = {
  low_engagement: { color: '#FA5252', bgColor: '#FFF5F5', icon: 'IconDown' },
  on_track: { color: '#4C6EF5', bgColor: '#EDF2FF', icon: 'BlueArrowRight' },
  high_engagement: { color: '#40C057', bgColor: '#EBFBEE', icon: 'GreenUpIcon' },
};

export const StreamDetailsModal: React.FC<StreamDetailsModalProps> = ({
  programmeId,
  streamId,
  onClose,
  breadcrumbLabel = 'Initiative insights',
}) => {
  const apiClient = useApiClient();
  const [activeTab, setActiveTab] = useState<'cohorts' | 'behaviours'>('cohorts');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortColumn>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const pageSize = 20;

  const { data, isLoading } = useQuery<StreamDetailsResponse>({
    queryKey: ['streamDetails', programmeId, streamId, page, sortBy, sortDir],
    queryFn: async () => {
      const res =
        await apiClient.api.getInitiativeStreamDetailsApiV1InsightsInitiativeProgrammeIdStreamsStreamIdGet(
          programmeId,
          streamId,
          { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir },
        );
      return res?.data;
    },
  });

  const handleSort = (col: SortColumn) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (col: SortColumn) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const header = data?.cohorts_overview?.header;
  const cohorts = data?.cohorts_overview?.cohorts ?? [];
  const totalCount = data?.cohorts_overview?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const behaviours = data?.behaviours_overview ?? [];

  const engagementStatusLabel = (status: string) => {
    if (status === 'ON_TRACK') return 'ON TRACK';
    if (status === 'NEEDS_ATTENTION') return 'NEEDS ATTENTION';
    return status.replace(/_/g, ' ');
  };

  const engagementStatusIsGood = (status: string) => status === 'ON_TRACK';

  const streamName = data?.stream_name ?? '...';

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages: (number | string)[] = [];
    for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
    if (totalPages > 5) {
      pages.push('...');
      pages.push(totalPages);
    }
    return (
      <div className={styles.pagination}>
        {pages.map((p, idx) =>
          typeof p === 'string' ? (
            <span key={idx} className={styles.pageEllipsis}>{p}</span>
          ) : (
            <button
              key={p}
              className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ),
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      contentLabel="Stream Details"
      ariaHideApp={false}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{streamName} stream details</h2>
        <button className={styles.closeX} onClick={onClose}>
          <Icon iconName="CrossIcon" width="1.5rem" height="1.5rem" />
        </button>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.navSection}>
          {/* Breadcrumb */}
          <div className={styles.breadcrumb}>
            <button className={styles.breadcrumbLink} onClick={onClose}>
              {breadcrumbLabel}
            </button>
            <span className={styles.breadcrumbSep}>/</span>
            <span className={styles.breadcrumbCurrent}>
              {streamName} stream details
            </span>
          </div>

          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'cohorts' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('cohorts')}
            >
              Cohorts overview
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'behaviours' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('behaviours')}
            >
              Behaviours overview
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={styles.scrollArea}>
        {isLoading && <div className={styles.loading}>Loading...</div>}

        {/* Cohorts Tab */}
        {activeTab === 'cohorts' && data && (
          <>
            {header && (
              <div className={styles.cohortSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>ACTIVE COHORTS</span>
                  <span className={styles.summaryValue}>{header.active_cohorts}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>TOTAL USERS</span>
                  <span className={styles.summaryValue}>{header.total_users}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>AVG COMPLETION</span>
                  <span className={styles.summaryValue}>{header.avg_completion}%</span>
                </div>
                <div className={styles.summaryItemProgress}>
                  <RingProgress
                    size={60}
                    thickness={5}
                    roundCaps
                    rootColor="#E9ECEF"
                    sections={[
                      {
                        value: header.active_users_pct,
                        color: engagementStatusIsGood(header.engagement_status) ? '#40C057' : '#FA5252',
                      },
                    ]}
                    styles={{ root: { transform: 'scale(1.22)', transformOrigin: 'center' } }}
                    label={
                      <div className={styles.ringIcon} style={{ color: engagementStatusIsGood(header.engagement_status) ? '#12B886' : '#FA5252' }}>
                        <Icon
                          iconName={engagementStatusIsGood(header.engagement_status) ? 'BlackArrowUp' : 'BlackArrowDown'}
                          width="0.75rem"
                          height="0.75rem"
                        />
                      </div>
                    }
                  />
                  <div className={styles.progressMeta}>
                    <span className={styles.progressMetaLabel}>
                      Active users: {header.active_users_pct}%
                    </span>
                    <span
                      className={styles.engagementBadge}
                      style={{
                        color: engagementStatusIsGood(header.engagement_status) ? '#40C057' : '#FA5252',
                        backgroundColor: engagementStatusIsGood(header.engagement_status) ? '#EBFBEE' : '#f9e5e6',
                      }}
                    >
                      {engagementStatusLabel(header.engagement_status)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.tableWrapper}>
              <table className={styles.cohortTable}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className={styles.sortable}>
                      Cohort{sortIndicator('name')}
                    </th>
                    <th onClick={() => handleSort('size')} className={styles.sortable}>
                      Size{sortIndicator('size')}
                    </th>
                    <th onClick={() => handleSort('start_date')} className={styles.sortable}>
                      Start date{sortIndicator('start_date')}
                    </th>
                    <th onClick={() => handleSort('end_date')} className={styles.sortable}>
                      End date{sortIndicator('end_date')}
                    </th>
                    <th>Module</th>
                    <th>% active users</th>
                    <th>Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={styles.emptyText}>No cohorts found</td>
                    </tr>
                  ) : (
                    cohorts.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>{c.size}</td>
                        <td>{formatDate(c.start_date)}</td>
                        <td>{formatDate(c.end_date)}</td>
                        <td>{c.current_module ?? '--'}</td>
                        <td>{c.active_users_pct}%</td>
                        <td>
                          <div className={styles.completionCell}>
                            <span>{c.completion_rate}%</span>
                            <div className={styles.completionBar}>
                              <div
                                className={styles.completionFill}
                                style={{ width: `${c.completion_rate}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {renderPagination()}
          </>
        )}

        {/* Behaviours Tab */}
        {activeTab === 'behaviours' && data && (
          <>
            {behaviours.length === 0 ? (
              <p className={styles.emptyText}>No behaviours data available</p>
            ) : (
              behaviours.map((mod) => (
                <div key={mod.module_number} className={styles.moduleCard}>
                  <h3 className={styles.moduleName}>{mod.module_name}</h3>
                  <div className={styles.moduleStats}>
                    <div className={styles.moduleStat}>
                      <span className={styles.moduleStatLabel}>Days</span>
                      <span className={styles.moduleStatValue}>{mod.days}</span>
                    </div>
                    <div className={styles.moduleStat}>
                      <span className={styles.moduleStatLabel}>Behaviours</span>
                      <span className={styles.moduleStatValue}>{mod.behaviours_count}</span>
                    </div>
                    <div className={styles.moduleStat}>
                      <span className={styles.moduleStatLabel}>Habits</span>
                      <span className={styles.moduleStatValue}>{mod.habits_count}</span>
                    </div>
                  </div>

                  <span className={styles.behavioursSectionLabel}>Behaviours</span>

                  {mod.behaviours.map((beh, bi) => {
                    const engStyle = ENGAGEMENT_STYLES[beh.engagement_status] ?? ENGAGEMENT_STYLES.on_track;
                    return (
                      <div key={bi} className={styles.behaviourCard}>
                        <div className={styles.behaviourHeader}>
                          <span className={styles.behaviourName}>{beh.name}</span>
                          <div className={styles.behaviourEngagement}>
                            <span className={styles.engagementText}>{beh.engagement_label}</span>
                            <RingProgress
                              size={60}
                              thickness={5}
                              roundCaps
                              rootColor="#E9ECEF"
                              sections={[
                                {
                                  value: Math.min(beh.avg_logs_per_week * 15, 100),
                                  color: engStyle.color,
                                },
                              ]}
                              styles={{ root: { transform: 'scale(1.22)', transformOrigin: 'center' } }}
                              label={
                                <div className={styles.ringIcon} style={{ color: engStyle.color }}>
                                  <Icon iconName={engStyle.icon} width="1rem" height="1rem" />
                                </div>
                              }
                            />
                          </div>
                        </div>
                        <div className={styles.habitsRow}>
                          {beh.habits.map((hab, hi) => (
                            <div
                              key={hi}
                              className={`${styles.habitCard} ${hi < beh.habits.length - 1 ? styles.habitCardDivider : ''}`}
                            >
                              <span className={styles.habitName}>Habit: {hab.name}</span>
                              <span className={styles.habitStatLabel}>Avg. logs / week</span>
                              <span className={styles.habitStatValue}>{hab.avg_logs_per_week}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </>
        )}
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};
